/**
 * @fileoverview Plan change confirmation modal component.
 * @summary Displays confirmation dialog before changing subscription plan.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/PlanChangeConfirmModal
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useTranslation } from 'react-i18next'
import { SubscriptionType, SUBSCRIPTION_PLANS } from '@karaonow/shared'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUp, faArrowDown, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import type { FC } from 'react'

interface PlanChangeConfirmModalProps {
  isOpen: boolean
  currentPlan: SubscriptionType
  newPlan: SubscriptionType
  onConfirm: () => void
  onCancel: () => void
  isLoading?: boolean
}

/**
 * @summary Plan change confirmation modal.
 * @param {PlanChangeConfirmModalProps} props - Component props.
 * @returns {ReactElement} Modal component.
 */
const PlanChangeConfirmModal: FC<PlanChangeConfirmModalProps> = ({
  isOpen,
  currentPlan,
  newPlan,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation()

  if (!isOpen) return null

  const currentPlanConfig = SUBSCRIPTION_PLANS[currentPlan]
  const newPlanConfig = SUBSCRIPTION_PLANS[newPlan]
  const currentPrice = currentPlanConfig.priceEur || 0
  const newPrice = newPlanConfig.priceEur || 0

  // Determine if it's an upgrade or downgrade
  const isUpgrade = newPrice > currentPrice
  const priceDifference = Math.abs(newPrice - currentPrice)

  // Get plan display names
  const getPlanDisplayName = (plan: SubscriptionType): string => {
    switch (plan) {
      case SubscriptionType.FREE:
        return t('subscription.planFree')
      case SubscriptionType.ONE_SHOT:
        return t('subscription.planOneShot')
      case SubscriptionType.PRO:
        return t('subscription.planPro')
      case SubscriptionType.PREMIUM:
        return t('subscription.planPremium')
      case SubscriptionType.ULTIMATE:
        return t('subscription.planUltimate')
      default:
        return plan
    }
  }

  const currentPlanName = getPlanDisplayName(currentPlan)
  const newPlanName = getPlanDisplayName(newPlan)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('subscription.changePlanTitle')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t('subscription.changePlanSubtitle')}
          </p>
        </div>

        {/* Plan Comparison */}
        <div className="mb-6 space-y-4">
          {/* Current Plan */}
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('subscription.currentPlan')}:
              </span>
              <span className="font-semibold text-gray-900 dark:text-white">{currentPlanName}</span>
            </div>
            {currentPrice > 0 && (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {currentPrice.toFixed(2)}€ / {t('subscription.perMonth')}
              </span>
            )}
          </div>

          {/* Arrow */}
          <div className="flex justify-center">
            <FontAwesomeIcon
              icon={isUpgrade ? faArrowUp : faArrowDown}
              className={`text-2xl ${isUpgrade ? 'text-green-500' : 'text-blue-500'}`}
            />
          </div>

          {/* New Plan */}
          <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                {t('subscription.newPlan')}:
              </span>
              <span className="font-semibold text-blue-900 dark:text-blue-100">{newPlanName}</span>
            </div>
            {newPrice > 0 && (
              <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                {newPrice.toFixed(2)}€ / {t('subscription.perMonth')}
              </span>
            )}
          </div>

          {/* Price Difference */}
          {priceDifference > 0 && (
            <div className={`p-3 rounded-lg ${isUpgrade ? 'bg-green-50 dark:bg-green-900/20' : 'bg-blue-50 dark:bg-blue-900/20'}`}>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className={isUpgrade ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}
                />
                <span className={`text-sm font-medium ${isUpgrade ? 'text-green-800 dark:text-green-200' : 'text-blue-800 dark:text-blue-200'}`}>
                  {isUpgrade
                    ? t('subscription.upgradeCost', { amount: priceDifference.toFixed(2) })
                    : t('subscription.downgradeCredit', { amount: priceDifference.toFixed(2) })}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Important Information */}
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
          <div className="flex items-start gap-2">
            <FontAwesomeIcon icon={faInfoCircle} className="text-amber-600 dark:text-amber-400 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-200">
              <p className="font-semibold mb-1">{t('subscription.immediateChange')}</p>
              <p>{t('subscription.immediateChangeDescription')}</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isUpgrade
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="inline-block animate-spin">⏳</span>
                {t('common.processing')}
              </span>
            ) : (
              t('subscription.confirmChange')
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PlanChangeConfirmModal

